import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import { FormDataType, TempFileItem } from './types'
import history from '../../utils/history'

function* fetchContest(): SagaIterator {
  try {
    const contests = yield effects.call(api.fetchContest)
    yield effects.put(actions.fetchContestSuccess(contests.data))
  } catch (error) {
    console.warn('Cannot fetch contest', error)
    yield effects.put(actions.fetchContestFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* sendWork(payload: { data: FormDataType }): SagaIterator {
  try {
    yield effects.call(api.sendWork, payload.data)
    yield effects.put(actions.sendWorkSuccess())
    yield effects.call([history, history.push], '/profile/contests')
  } catch (error) {
    console.warn('Cannot send work', error)
    yield effects.put(actions.sendWorkFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* updateWork(payload: {
  work_id: string
  data: FormDataType
}): SagaIterator {
  try {
    const { work_id, data } = payload
    yield effects.call(api.updateWork, work_id, data)
    yield effects.put(actions.updateWorkSuccess())
    yield effects.call([history, history.push], '/profile/contests')
  } catch (error) {
    console.warn('Cannot update work', error)
    yield effects.put(actions.updateWorkFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* uploadFile(payload: {
  file: File
  uploadProcess: (percent: number) => void
  uploadedFile: (uploadedFile: TempFileItem) => void
}): SagaIterator {
  try {
    const { file, uploadProcess, uploadedFile } = payload
    const fileData = yield effects.call(api.uploadFile, file, uploadProcess)
    uploadedFile(fileData.data)
    yield effects.put(actions.uploadFileSuccess())
  } catch (error) {
    console.warn('Cannot upload files', error)
    yield effects.put(actions.uploadFileFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeFile(payload: { file_id: string }): SagaIterator {
  try {
    yield effects.call(api.removeFile, payload.file_id)
    yield effects.put(actions.removeFileSuccess(payload.file_id))
  } catch (error) {
    console.warn('Cannot remove file', error)
    yield effects.put(actions.removeFileFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeFileFromWork(payload: {
  wid: string
  file_id: string
}): SagaIterator {
  try {
    const { wid, file_id } = payload
    yield effects.call(api.removeFileFromWork, wid, file_id)
    yield effects.put(actions.removeFileFromWorkSuccess(file_id))
  } catch (error) {
    console.warn('Cannot remove file from work', error)
    yield effects.put(
      actions.removeFileFromWorkFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* fetchMyWork(payload: { work_id: string }): SagaIterator {
  try {
    const work = yield effects.call(api.fetchMyWork, payload.work_id)
    yield effects.put(actions.fetchMyWorkSuccess(work.data))
  } catch (error) {
    console.warn('Cannot fetch my work', error)
    yield effects.put(actions.fetchMyWorkFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchContest(): SagaIterator {
  yield effects.takeEvery(actions.fetchContest.toString(), fetchContest)
}

function* watchSendWork(): SagaIterator {
  yield effects.takeEvery(actions.sendWork.toString(), sendWork)
}

function* watchUpdateWork(): SagaIterator {
  yield effects.takeEvery(actions.updateWork.toString(), updateWork)
}

function* watchUploadFile(): SagaIterator {
  yield effects.takeEvery(actions.uploadFile.toString(), uploadFile)
}

function* watchRemoveFile(): SagaIterator {
  yield effects.takeEvery(actions.removeFile.toString(), removeFile)
}

function* watchRemoveFileFromWork(): SagaIterator {
  yield effects.takeEvery(
    actions.removeFileFromWork.toString(),
    removeFileFromWork
  )
}

function* watchFetchMyWork(): SagaIterator {
  yield effects.takeEvery(actions.fetchMyWork.toString(), fetchMyWork)
}

export default function* watchProfileContestWork(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchContest),
    effects.fork(watchSendWork),
    effects.fork(watchUpdateWork),
    effects.fork(watchUploadFile),
    effects.fork(watchRemoveFile),
    effects.fork(watchRemoveFileFromWork),
    effects.fork(watchFetchMyWork),
  ])
}
